<template>
  <div>
    <h3 class="weight-medium pb-10 pb-md-16 text-left text-md-center">
      Enter Information on the Residence of the Legal Representative
    </h3>

    <div class="flex justify-md-center align-center pb-10">
      <h4 class="weight-medium text-left pr-6">
        Permanent Residence Address
        <Tooltip slot="tooltip">
          <p class="text-left m-0">
            
    Your <b>permanent residence address</b> is the place in the Slovak Republic where you are staying long-term.
    You can only have one permanent residence address, and it must be stated on your ID card.
    <br />
    <br />
    In the case of a child, the permanent residence address is the same as the permanent residence
    address of the legal representative.
  
          </p>
        </Tooltip>
      </h4>
    </div>

    <div class="input-box-holder" v-validate="errors"
         v-has-value="legalRepresentative.isAddressSameAsChild">
      <div class="validation-output text-left mb-5">
        <ErrorHandler class="error" :id="`V281_${index}`">
          You have not selected any of the options. To continue, please select one option.
        </ErrorHandler>
      </div>
      <div class="block">
        <BooleanRadioButton
          class="body-2 weight-regular my-5"
          title='Same as the Child'
          :name="'legalRepresentativeAddress' + personIndex"
          :option="true"
          :value="legalRepresentative.isAddressSameAsChild"
          @input="setAddressSameAsChild"
        />
        <BooleanRadioButton
          class="body-2 weight-regular my-5"
          title='Different from the Child'
          :name="'legalRepresentativeAddress' + personIndex"
          :option="false"
          :value="legalRepresentative.isAddressSameAsChild"
          @input="setAddressSameAsChild"
        />
      </div>
    </div>

    <PermanentAddress
      v-if="showPermanentAddressSection"
      :address="legalRepresentative.address"
      :errors="errors"
      :index="personIndex"
    />

    <div class="flex justify-md-center align-center pt-16 pb-10">
      <h4 class="weight-medium text-left pr-6">
        Mailing Address
        <Tooltip slot="tooltip">
          <p class="text-left m-0">
            
    <b>The mailing address</b> or delivery address is the place in the Slovak Republic where you
    wish to receive all written documents.
    <br />
    <br />
    Please enter the mailing address if you wish to receive documents at an address different from
    your permanent residence address.
  
          </p>
        </Tooltip>
      </h4>
    </div>

    <Checkbox
      class="cb-card heading-4 weight-medium"
      title='Different from Your Permanent Residence Address'
      @input="setPostallAddresSame"
      :value="!legalRepresentative.postalAddressSameAsAddress"
      :checked="!legalRepresentative.postalAddressSameAsAddress"
    >
      <PostalAddress
        v-if="!legalRepresentative.postalAddressSameAsAddress"
        :address="legalRepresentative.postalAddress"
        :errors="errors"
        :index="personIndex"
        :person="legalRepresentative"
      />
    </Checkbox>

    <div class="flex justify-md-center align-center pt-16 pb-10">
      <h4 class="weight-medium text-left pr-6">
        Temporary Residence Address
        <Tooltip slot="tooltip">
          <p class="text-left m-0">
            
    <b>A temporary residence address</b> is a place in the Slovak Republic different from your permanent
    residence address where you are staying for more than 90 days a year.
    <br />
    <br />
    Enter a temporary residence address if:
    <ul class="pl-8">
        <li>
            you do not regularly stay at your permanent residence address
        </li>
        <li>
            you are a citizen with a permanent residence abroad and your temporary stay in the territory
            of the Slovak Republic is longer than 90 days
        </li>
    </ul>
  
          </p>
        </Tooltip>
      </h4>
    </div>

    <Checkbox
      class="cb-card heading-4 mb-10 weight-medium"
      title='The Legal Representative Has Temporary Residence'
      v-model="legalRepresentative.hasTemporaryResidence"
      :checked="legalRepresentative.hasTemporaryResidence"
    >
      <TemporaryAddress
        v-if="legalRepresentative.hasTemporaryResidence"
        :address="legalRepresentative.temporaryResidence"
        :errors="errors"
        :index="personIndex"
      />
    </Checkbox>

    <div v-if="child.insuredPersonType !== insuranceReasonType.CHANGE_OF_INSURANCE_COMPANY_CHILD">
      <div class="flex justify-md-center align-center pt-6 pb-10">
        <h4 class="weight-medium text-left pr-6">
          Do you wish to also indicate another legal representative for the child?
        </h4>
      </div>

      <div
        class="input-box-holder mb-10"
        v-validate="errors"
        v-has-value="secondLegalRepresentative.iAmInterestedEnterSecondLegalRepresentative"
      >
        <DoubleInlineRadioButton
          :name="'iAmInterestedEnterSecondLegalRepresentativeGroup' + personIndex"
          second-title='No'
          first-title='Yes'
          v-model="secondLegalRepresentative.iAmInterestedEnterSecondLegalRepresentative"
        />
        <div class="validation-output text-left mt-3">
          <ErrorHandler class="error" :id="`V248_${index}`">
            Select one of the options.
          </ErrorHandler>
        </div>
      </div>

      <SecondLegalRepresentative
        v-if="secondLegalRepresentative.iAmInterestedEnterSecondLegalRepresentative"
        :model="model"
        :index="index"
        :errors="errors"
        :legal-representative="secondLegalRepresentative"
        :insured-person="child"
      />
      <div
        v-else-if="secondLegalRepresentative.iAmInterestedEnterSecondLegalRepresentative === false">
        <div class="flex justify-md-center align-center pt-6 pb-10">
          <h4 class="weight-medium text-left pr-6">
            Is the other legal representative listed on the child's birth certificate?
          </h4>
        </div>
        <div
          class="input-box-holder mb-10"
          v-validate="errors"
          v-has-value="secondLegalRepresentative.isSecondLegalRepresentativeInBirthList"
        >
          <DoubleInlineRadioButton
            :name="'isSecondLegalRepresentativeInBirthListGroup' + personIndex"
            second-title='No'
            first-title='Yes'
            v-model="secondLegalRepresentative.isSecondLegalRepresentativeInBirthList"
          />
          <div class="validation-output text-left mt-3">
            <ErrorHandler class="error" :id="`V249_${index}`">
              Select one of the options.
            </ErrorHandler>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>
<script>
import Checkbox from '@/components/Checkbox/Checkbox.vue';
import PermanentAddress from '@/pagesComponents/AddressComponents/PermanentAddress.vue';
import PostalAddress from '@/pagesComponents/AddressComponents/PostalAddress.vue';
import TemporaryAddress from '@/pagesComponents/AddressComponents/TemporaryAddress.vue';
import persons from '@/mixins/persons';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import BooleanRadioButton from '@/components/BooleanRadioButton/RadioButton.vue';
import DoubleInlineRadioButton from '@/components/DoubleInlineRadioButton/DoubleInlineRadioButton.vue';
import ErrorHandler from '@/components/ErrorHandler/ErrorHandler.vue';
import address from '@/mixins/address';
import SecondLegalRepresentative from '@/pagesComponents/SecondLegalRepresentative.vue';

export default {
  components: {
    SecondLegalRepresentative,
    ErrorHandler,
    DoubleInlineRadioButton,
    BooleanRadioButton,
    PermanentAddress,
    PostalAddress,
    TemporaryAddress,
    Checkbox,
    Tooltip,
  },
  mixins: [persons, address],
  props: {
    legalRepresentative: { type: Object, required: true },
    secondLegalRepresentative: { type: Object, required: true },
    model: { type: Object, required: true },
    errors: { type: Array, required: true },
    personIndex: { type: Number, required: true },
    index: { type: Number, required: true },
    child: { type: Object, required: true },
  },
  methods: {
    setPostallAddresSame(value) {
      this.legalRepresentative.postalAddressSameAsAddress = !value;
    },
    setAddressSameAsChild(isSame) {
      if (isSame) {
        this.legalRepresentative.address = this.child.address;
      } else {
        this.legalRepresentative.address = this.createNewAddress();
      }
      this.legalRepresentative.isAddressSameAsChild = isSame;
    },
  },
  computed: {
    showPermanentAddressSection() {
      return this.legalRepresentative.isAddressSameAsChild !== null && !this.legalRepresentative.isAddressSameAsChild;
    },
    insuranceReasonType() {
      const { InsuredPersonTypeEnum: enumeration } = this.enums;
      return enumeration;
    },
  },
};
</script>
